<script>
import { createTypingIndicator } from '@chatwoot/utils';

const TYPING_INDICATOR_IDLE_TIME = 4000;
export default {
  props: {
    placeholder: {
      type: String,
      default: '',
    },
    value: {
      type: String,
      default: '',
    },
    minHeight: {
      type: Number,
      default: 2,
    },
    rows: {
      type: Number,
      default: 2,
    },
    focusOnMount: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      initialHeight: null,
      typingIndicator: createTypingIndicator(
        () => {
          this.$emit('typingOn');
        },
        () => {
          this.$emit('typingOff');
        },
        TYPING_INDICATOR_IDLE_TIME
      ),
    };
  },
  watch: {
    value() {
      this.resizeTextarea();
      // 🚨 watch triggers every time the value is changed, we cannot set this to focus then
      // when this runs, it sets the cursor to the end of the body
      // Suppose if someone manually set the cursor to the middle of the body
      // and starts typing, the cursor will be set to the end of the body
      // A surprise cursor jump? Definitely not user-friendly.
      if (document.activeElement !== this.$refs.textarea) {
        this.$nextTick(() => {
          this.setCursor();
        });
      }
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.resizeTextarea();

      if (this.value && this.focusOnMount) {
        this.setCursor();
      } else if (this.focusOnMount) {
        this.focus();
      }
    });
  },
  methods: {
    resizeTextarea() {
      const textarea = this.$refs.textarea;

      if (!textarea) {
        return;
      }

      const minHeightInPixels = this.minHeight * 16;

      if (!this.initialHeight) {
        this.initialHeight = Math.max(textarea.offsetHeight, minHeightInPixels);
      }

      textarea.style.height = 'auto';
      textarea.style.height = `${Math.max(
        textarea.scrollHeight,
        this.initialHeight
      )}px`;
    },
    setCursor() {
      // only trim at end, so if there are spaces at the start, those are not removed
      const cursorPosition = this.value.trimEnd().length;
      const textarea = this.$refs.textarea;

      if (textarea) {
        textarea.focus();
        textarea.setSelectionRange(cursorPosition, cursorPosition);
      }
    },
    onInput(event) {
      this.$emit('input', event.target.value);
      this.resizeTextarea();
    },
    onKeyup() {
      this.typingIndicator.start();
    },
    onBlur() {
      this.typingIndicator.stop();
      this.$emit('blur');
    },
    onFocus() {
      this.$emit('focus');
    },
    focus() {
      if (this.$refs.textarea) this.$refs.textarea.focus();
    },
  },
};
</script>

<template>
  <textarea
    ref="textarea"
    :placeholder="placeholder"
    :rows="rows"
    :value="value"
    @input="onInput"
    @focus="onFocus"
    @keyup="onKeyup"
    @blur="onBlur"
  />
</template>
